import React, { useMemo } from 'react';
import { AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus, Heart, Target, Award, Dumbbell, MessageSquare, ChevronDown, CheckCircle, AlertCircle, Repeat, Star, Timer, Info } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

const PerformanceMetricCard = ({ title, value, icon: Icon, tooltip }) => (
    <Card className="bg-muted">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground flex items-center">
                {title}
                {tooltip && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Info className="h-3 w-3 text-muted-foreground ml-1.5 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{tooltip}</p>
                        </TooltipContent>
                    </Tooltip>
                )}
            </CardTitle>
            <Icon className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
            <div className="text-xl font-bold">{value}</div>
        </CardContent>
    </Card>
);


const ExerciseCard = ({ exercise, index }) => {

  const compliance = useMemo(() => {
    if (!exercise.prescribed_sets || exercise.prescribed_sets === 0) return { percent: null, setsRatio: '0/0' };
    const completedSets = exercise.sets_data?.length || 0;
    const percent = Math.round((completedSets / exercise.prescribed_sets) * 100);
    return {
        percent,
        setsRatio: `${completedSets}/${exercise.prescribed_sets}`
    };
  }, [exercise]);

  const performanceMetrics = useMemo(() => {
    const sets = exercise.sets_data || [];
    const totalReps = sets.reduce((acc, set) => acc + (set.reps || 0), 0);
    const totalVolume = sets.reduce((acc, set) => acc + ((set.reps || 0) * (set.weight || 0)), 0);
    
    // This is a placeholder as time per exercise is not available
    const timeToComplete = 'N/A';

    return {
        totalReps,
        totalVolume,
        difficultyRating: exercise.difficulty_rating,
        timeToComplete,
    };
  }, [exercise]);

  const VolumeTrendIcon = () => {
    if (!exercise.volume_trend) return <Minus className="h-5 w-5 text-muted-foreground" />;
    const { current, previous } = exercise.volume_trend;

    if (current > 0 && previous === 0) return <TrendingUp className="h-5 w-5 text-green-500" />;
    if (current === 0) return <Minus className="h-5 w-5 text-muted-foreground" />;
    if (current > previous) return <TrendingUp className="h-5 w-5 text-green-500" />;
    if (current < previous) return <TrendingDown className="h-5 w-5 text-red-500" />;
    return <Minus className="h-5 w-5 text-muted-foreground" />;
  };

  const PainBadgeContent = () => {
    const pain = exercise.pain_level_reported;
    if (pain === null || pain === undefined) return <span className="text-muted-foreground">N/A</span>;
    let color = 'text-green-700 bg-green-100/50';
    if (pain > 6) color = 'text-red-700 bg-red-100/50';
    else if (pain > 3) color = 'text-orange-700 bg-orange-100/50';
    else if (pain > 0) color = 'text-yellow-700 bg-yellow-100/50';
    
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <div className="flex items-center">
                    <Heart className={cn("h-3 w-3 mr-1.5", color.split(' ')[0])} />
                    <span className={cn(color.split(' ')[0])}>{pain}/10</span>
                </div>
            </TooltipTrigger>
            <TooltipContent><p>Pain Level</p></TooltipContent>
        </Tooltip>
    );
  };
  
  const currentNotes = (exercise.notes || exercise.exercise_notes);

  const StatusBadge = () => {
    if (exercise.completed_at) {
        return (
            <Badge variant="success" className="flex items-center gap-1.5">
                <CheckCircle className="h-3 w-3" />
                Completed
            </Badge>
        );
    }
    return (
        <Badge variant="default" className="flex items-center gap-1.5">
            <AlertCircle className="h-3 w-3" />
            Not Logged
        </Badge>
    );
  };


  return (
    <>
      <AccordionTrigger className="w-full p-4 bg-muted hover:bg-muted rounded-lg data-[state=open]:rounded-b-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-all group">
        <div className="w-full">
            <div className="flex justify-between items-start w-full mb-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Badge variant="secondary" className="text-sm flex-shrink-0">#{exercise.order_in_session}</Badge>
                    <p className="font-semibold text-base md:text-lg text-foreground truncate flex items-center gap-2">
                        {exercise.exercise_name}
                    </p>
                </div>
                <div className="flex items-center gap-3 ml-2">
                    {exercise.volume_trend && exercise.volume_trend.previous > 0 && exercise.volume_trend.current > 0 && (
                        <Tooltip>
                            <TooltipTrigger asChild><VolumeTrendIcon /></TooltipTrigger>
                            <TooltipContent>
                                <p>Volume Trend vs. Last Workout</p>
                                <p className="text-xs text-muted-foreground">Current: {exercise.volume_trend?.current.toFixed(1)}kg | Previous: {exercise.volume_trend?.previous.toFixed(1)}kg</p>
                            </TooltipContent>
                        </Tooltip>
                    )}
                    <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform duration-300 group-data-[state=open]:rotate-180" />
                </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge />
                {currentNotes && currentNotes.trim() !== '' && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-500/10 text-blue-500 text-xs font-bold">
                                <MessageSquare className="h-3 w-3" />
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>User Notes: {currentNotes}</p>
                        </TooltipContent>
                    </Tooltip>
                )}
                
                <Tooltip>
                    <TooltipTrigger asChild>
                         <Badge variant="outline" className="flex items-center">
                            <Target className="h-3 w-3 mr-1.5" /> {exercise.rpe_reported ?? 'N/A'}
                        </Badge>
                    </TooltipTrigger>
                    <TooltipContent><p>Rate of Perceived Exertion (RPE)</p></TooltipContent>
                </Tooltip>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Badge variant="outline" className="flex items-center">
                            <Award className="h-3 w-3 mr-1.5" /> {exercise.form_rating ?? 'N/A'}
                        </Badge>
                    </TooltipTrigger>
                    <TooltipContent><p>Form Rating</p></TooltipContent>
                </Tooltip>
                 <Tooltip>
                    <TooltipTrigger asChild>
                        <Badge variant="outline" className="flex items-center">
                            <Dumbbell className="h-3 w-3 mr-1.5" /> {compliance.setsRatio}
                        </Badge>
                    </TooltipTrigger>
                     <TooltipContent><p>Sets Completed / Prescribed</p></TooltipContent>
                </Tooltip>
            </div>
            {currentNotes && (
                <div className="mt-3 bg-amber-500/10 border border-amber-500/20 text-amber-500 text-xs rounded-md p-2 flex items-start gap-2">
                     <MessageSquare className="h-4 w-4 flex-shrink-0 text-amber-500 mt-0.5" />
                    <span>{currentNotes}</span>
                </div>
            )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="bg-card p-4 rounded-b-lg border-t-2 space-y-6">
        
        {(exercise.therapeutic_intent || exercise.tissue_target) && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <h4 className="font-semibold text-blue-500 mb-1 text-sm">Therapeutic Intent</h4>
                        <p className="text-blue-400 text-sm">{exercise.therapeutic_intent || 'Not specified'}</p>
                    </div>
                    <div>
                        <h4 className="font-semibold text-blue-500 mb-1 text-sm">Tissue Target</h4>
                        <p className="text-blue-400 text-sm">{exercise.tissue_target || 'Not specified'}</p>
                    </div>
                </div>
            </div>
        )}

        <div>
            <h4 className="font-semibold text-foreground mb-2">Set Breakdown</h4>
            <div className="border rounded-lg overflow-x-auto">
                <Table>
                    <TableHeader className="bg-muted">
                        <TableRow>
                            <TableHead className="w-[50px] px-2 sm:px-4">Set</TableHead>
                            <TableHead className="px-2 sm:px-4">Prescribed</TableHead>
                            <TableHead className="px-2 sm:px-4">Actual</TableHead>
                            <TableHead className="px-2 sm:px-4">Load</TableHead>
                            <TableHead className="px-2 sm:px-4">Volume</TableHead>
                            <TableHead className="text-right px-2 sm:px-4">Status</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {exercise.sets_data && exercise.sets_data.length > 0 ? (
                            exercise.sets_data.map((set, i) => {
                                const volume = (set.reps || 0) * (set.weight || 0);
                                const hitTarget = (set.reps || 0) >= (exercise.prescribed_reps || 0);
                                return (
                                    <TableRow key={i} className="hover:bg-muted/50">
                                        <TableCell className="font-medium px-2 sm:px-4">{set.set_number || i + 1}</TableCell>
                                        <TableCell className="px-2 sm:px-4">{exercise.prescribed_reps || 'N/A'} reps</TableCell>
                                        <TableCell className="px-2 sm:px-4">{set.reps || 'N/A'} reps</TableCell>
                                        <TableCell className="px-2 sm:px-4 whitespace-nowrap">{set.weight || 'N/A'} {set.unit || 'kg'}</TableCell>
                                        <TableCell className="px-2 sm:px-4 whitespace-nowrap">{volume.toFixed(1)} kg</TableCell>
                                        <TableCell className="text-right px-2 sm:px-4">
                                            <Tooltip>
                                                <TooltipTrigger>
                                                    {hitTarget ? <CheckCircle className="h-5 w-5 text-green-500 inline" /> : <AlertCircle className="h-5 w-5 text-orange-500 inline" />}
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>{hitTarget ? 'Met or exceeded prescribed reps' : 'Below prescribed reps'}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center text-muted-foreground h-24">
                                    No sets were logged for this exercise.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                    {performanceMetrics.totalVolume > 0 && (
                        <TableFooter>
                            <TableRow>
                                <TableCell colSpan={4} className="font-bold text-right px-2 sm:px-4">Total Volume</TableCell>
                                <TableCell className="font-bold px-2 sm:px-4 whitespace-nowrap">{performanceMetrics.totalVolume.toFixed(1)} kg}</TableCell>
                                <TableCell className="px-2 sm:px-4"></TableCell>
                            </TableRow>
                        </TableFooter>
                    )}
                </Table>
            </div>
        </div>

        <div>
            <h4 className="font-semibold text-foreground mb-2">Performance Metrics</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <PerformanceMetricCard title="Total Reps" value={performanceMetrics.totalReps} icon={Repeat} tooltip="Total repetitions completed for this exercise." />
                <PerformanceMetricCard title="Difficulty" value={performanceMetrics.difficultyRating ? `${performanceMetrics.difficultyRating}/5` : 'N/A'} icon={Star} tooltip="User-reported difficulty rating."/>
                <PerformanceMetricCard title="Time" value={performanceMetrics.timeToComplete} icon={Timer} tooltip="Time to complete exercise (placeholder)."/>
            </div>
        </div>

      </AccordionContent>
    </>
  );
};

export default ExerciseCard;